import { format } from "date-fns";

import { AppointmentSlotPreferenceTier, type AvailabilityAppointmentSlot } from "@/shared";
import { formatAbsoluteTime, formatRelativeDateHeading } from "@/src/lib/utils/date-and-phone-utils";
import type { AppointmentSlot } from "@/src/models/comms/scheduling-schemas";
import { getPersonName } from "@/src/models/person";

/**
 * A UI-facing appointment slot, normalized from either the authenticated dashboard
 * `AppointmentSlot` (nested practitioner/location models, keyed on `ehrId`) or the public /
 * agent `AvailabilityAppointmentSlot` (flat ids/names, keyed on `appointmentId`). Both
 * scheduling surfaces render the same shared picker by mapping to this shape first.
 * Public provider-first scheduling may also supply a patient-facing location label for each slot.
 *
 * `start` is an ISO string that already carries the clinic's UTC offset, so day grouping and
 * time display use the wall-clock date/time from the string rather than the visitor's browser
 * timezone.
 */
export interface NormalizedSlot {
  /** Stable id used to round-trip a selection back to the source slot. */
  id: string;
  start: string;
  providerDisplayName?: string | null;
  locationDisplayName?: string | null;
  /** Staff-facing "preferred slot" flag; undefined for surfaces that don't rank slots. */
  isPreferred?: boolean;
  /** Preserve server ordering within each day when supplied. */
  displayOrder?: number;
}

export interface SlotDayGroup {
  dateKey: string;
  date: Date;
  slots: NormalizedSlot[];
}

/** Clinic-local calendar day ("YYYY-MM-DD") from an ISO datetime's wall-clock date prefix. */
export function slotDateKey(iso: string): string {
  // Full datetimes carry the clinic offset in the string; date-only inputs are already YYYY-MM-DD.
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

export function dateFromKey(key: string): Date {
  const [year, month, day] = key.split("-").map(Number);

  return new Date(year, month - 1, day);
}

export function dateKeyFromDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** "Tomorrow at 9:00 AM" — the relative day heading plus wall-clock time for a slot's ISO start. */
export function formatSlotDateTime(startIso: string): string {
  return `${formatRelativeDateHeading(slotDateKey(startIso))} at ${formatAbsoluteTime(startIso)}`;
}

/** Group by clinic-local day, preserving supplied order or sorting preferred-first then chronologically. */
export function groupSlotsByDay(slots: NormalizedSlot[]): SlotDayGroup[] {
  const byDay = new Map<string, NormalizedSlot[]>();

  for (const slot of slots) {
    const key = slotDateKey(slot.start);
    const existing = byDay.get(key);

    if (existing) {
      existing.push(slot);
    } else {
      byDay.set(key, [slot]);
    }
  }

  return [...byDay.entries()]
    .map(([dateKey, daySlots]) => ({
      dateKey,
      date: dateFromKey(dateKey),
      slots: daySlots.sort((a, b) => {
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) return a.displayOrder - b.displayOrder;

        if (a.isPreferred && !b.isPreferred) return -1;
        if (!a.isPreferred && b.isPreferred) return 1;

        return a.start.localeCompare(b.start);
      }),
    }))
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
}

/** Adapter: public / agent availability slot → shared picker shape. */
export function normalizeAvailabilitySlot(
  slot: AvailabilityAppointmentSlot,
  locationDisplayName: string | null | undefined = slot.locationName
): NormalizedSlot {
  return {
    id: slot.appointmentId,
    start: slot.start,
    isPreferred: slot.preferenceTier === AppointmentSlotPreferenceTier.Preferred,
    providerDisplayName: slot.practitionerDisplayName,
    locationDisplayName: locationDisplayName?.slice(locationDisplayName.indexOf("-") + 1).trim() || locationDisplayName,
  };
}

/** Adapter: authenticated dashboard `AppointmentSlot` → shared picker shape. */
export function normalizeAppointmentSlot(slot: AppointmentSlot): NormalizedSlot {
  return {
    id: slot.ehrId,
    start: slot.start,
    providerDisplayName: slot.practitioner ? getPersonName(slot.practitioner, false, false, true) : null,
    isPreferred: slot.isPreferredSlot,
  };
}
